import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { 
  Box, 
  Text, 
  Loader, 
  Grid, 
  Badge, 
  Accordion, 
  Checkbox, 
  Group,
  Container,
  Paper,
  ScrollArea,
  TextInput,
  Stack,
  Title,
  Button,
  Modal,
  ActionIcon,
  Menu
} from '@mantine/core';
import { IconSearch, IconPlus, IconDots, IconEdit, IconTrash } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { apiInstance } from '../services/api_service';
import { groupPermissionsByModel } from '../utils/permissions';
import { toast } from 'react-toastify';

const GroupDetails = ({ group, onGroupUpdate }) => {
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [groupPermissions, setGroupPermissions] = useState(group?.permissions || []);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [permissionSearch, setPermissionSearch] = useState('');
  const [loadingPermissions, setLoadingPermissions] = useState(new Set());
  const [accordionValue, setAccordionValue] = useState([]);

  // Update group permissions when group prop changes and reset accordion state
  useEffect(() => {
    setGroupPermissions(group?.permissions || []);
    setAccordionValue([]); // Reset accordion state when group changes
  }, [group]);

  // Helper function to get permission display name
  const getPermissionDisplayName = (permission) => {
    // Use actual_description if available, otherwise fall back to name
    return permission.actual_description || permission.name || permission.codename;
  };

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!group) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await apiInstance.get('authentication/permissions/');
        setAvailablePermissions(response.data);
      } catch (err) {
        console.error('Error fetching permissions:', err);
        setError('Erro ao carregar permissões');
        toast.error('Erro ao carregar permissões');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPermissions();
  }, [group]);

  const handlePermissionToggle = useCallback(async (permission, isChecked) => {
    const permissionKey = permission.codename;
    
    // Add to loading state
    setLoadingPermissions(prev => new Set([...prev, permissionKey]));
    
    try {
      if (isChecked) {
        // Add permission to group
        await apiInstance.post(`authentication/groups/${group.id}/grant-permission/`, {
          codename: permission.codename,
          content_type: permission.content_type
        });
        
        // Update local permissions state
        setGroupPermissions(prev => [...prev, permission]);
        
        toast.success(
          `Permissão "${getPermissionDisplayName(permission)}" adicionada ao grupo "${group.name}"`,
          {
            position: "top-right",
            autoClose: 1500,
          }
        );
      } else {
        // Remove permission from group
        await apiInstance.delete(`authentication/groups/${group.id}/revoke-permission/`, {
          data: {
            codename: permission.codename,
            content_type: permission.content_type
          }
        });
        
        // Update local permissions state
        setGroupPermissions(prev => prev.filter(p => p.codename !== permission.codename));
        
        toast.success(
          `Permissão "${getPermissionDisplayName(permission)}" removida do grupo "${group.name}"`,
          {
            position: "top-right",
            autoClose: 1500,
          }
        );
      }
    } catch (err) {
      console.error('Error toggling permission:', err);
      toast.error(`Erro ao ${isChecked ? 'adicionar' : 'remover'} permissão`);
    } finally {
      // Remove from loading state
      setLoadingPermissions(prev => {
        const newSet = new Set(prev);
        newSet.delete(permissionKey);
        return newSet;
      });
    }
  }, [group.id, group.name]); // Include group.id and group.name dependencies

  const isPermissionChecked = useCallback((permission) => {
    return groupPermissions?.some(p => p.codename === permission.codename) || false;
  }, [groupPermissions]);

  const filteredPermissions = useMemo(() => 
    availablePermissions.filter(permission =>
      getPermissionDisplayName(permission).toLowerCase().includes(permissionSearch.toLowerCase()) ||
      permission.codename.toLowerCase().includes(permissionSearch.toLowerCase()) ||
      `${permission.content_type.app_label}.${permission.content_type.model}`.toLowerCase().includes(permissionSearch.toLowerCase())
    ), [availablePermissions, permissionSearch]
  );

  const groupedAvailablePermissions = useMemo(() => 
    groupPermissionsByModel(filteredPermissions), 
    [filteredPermissions]
  );

  if (isLoading) {
    return (
      <Box display="flex" style={{ justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <Loader size="md" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p="md">
        <Text color="red">{error}</Text>
      </Box>
    );
  }

  return (
    <Container size="xl" p="md">
      <Stack spacing="md">
        <Title order={3}>Permissões do Grupo: {group.name}</Title>
        
        <TextInput
          placeholder="Buscar permissões..."
          icon={<IconSearch size="0.9rem" />}
          value={permissionSearch}
          onChange={(event) => setPermissionSearch(event.currentTarget.value)}
          mb="md"
        />

        <Text size="sm" color="dimmed" mb="xs">
          {(() => {
            const grantedCount = filteredPermissions.filter(permission => 
              isPermissionChecked(permission)
            ).length;
            return `Permissões concedidas: ${grantedCount} de ${filteredPermissions.length} disponíveis`;
          })()}
        </Text>

        <ScrollArea style={{ height: '60vh' }}>
          <Accordion 
            multiple 
            value={accordionValue} 
            onChange={setAccordionValue}
          >
            {Object.entries(groupedAvailablePermissions).map(([modelKey, modelData]) => {
              const grantedCount = modelData.permissions.filter(permission => 
                isPermissionChecked(permission)
              ).length;
              const totalCount = modelData.permissions.length;
              
              return (
                <Accordion.Item key={modelKey} value={modelKey}>
                  <Accordion.Control>
                    <Group position="apart">
                      <Text>{modelData.model_name}</Text>
                      <Badge variant="light" size="sm">
                        {grantedCount}/{totalCount}
                      </Badge>
                    </Group>
                  </Accordion.Control>
                <Accordion.Panel>
                  <Stack spacing="xs">
                    {modelData.permissions.map(permission => {
                      const isChecked = isPermissionChecked(permission);
                      const isLoading = loadingPermissions.has(permission.codename);
                      
                      return (
                        <Paper key={permission.codename} p="xs" radius="md" withBorder>
                          <Group position="apart">
                            <Box style={{ flex: 1 }}>
                              <Text size="sm" weight={500}>
                                {getPermissionDisplayName(permission)}
                              </Text>
                              
                            </Box>
                            <Checkbox
                              checked={isChecked}
                              onChange={(event) => 
                                handlePermissionToggle(permission, event.currentTarget.checked)
                              }
                              disabled={isLoading}
                            />
                          </Group>
                        </Paper>
                      );
                    })}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
              );
            })}
          </Accordion>
        </ScrollArea>
      </Stack>
    </Container>
  );
};

const CreateGroupModal = ({ opened, onClose, onGroupCreated }) => {
  const [groupName, setGroupName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!groupName.trim()) {
      toast.error('Nome do grupo é obrigatório');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await apiInstance.post('authentication/groups/', {
        name: groupName.trim()
      });
      
      toast.success(`Grupo "${response.data.name}" criado com sucesso!`);
      onGroupCreated(response.data);
      onClose();
      setGroupName('');
    } catch (err) {
      console.error('Error creating group:', err);
      toast.error('Erro ao criar grupo');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Criar Novo Grupo">
      <form onSubmit={handleSubmit}>
        <Stack spacing="md">
          <TextInput
            label="Nome do Grupo"
            placeholder="Digite o nome do grupo"
            value={groupName}
            onChange={(event) => setGroupName(event.currentTarget.value)}
            required
            maxLength={150}
          />
          <Group position="right">
            <Button variant="subtle" onClick={onClose} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" loading={isSubmitting}>
              Criar Grupo
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

const EditGroupModal = ({ opened, onClose, group, onGroupUpdated }) => {
  const [groupName, setGroupName] = useState(group?.name || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (group) {
      setGroupName(group.name);
    }
  }, [group]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!groupName.trim()) {
      toast.error('Nome do grupo é obrigatório');
      return;
    }

    if (groupName.trim() === group.name) {
      onClose();
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await apiInstance.patch(`authentication/groups/${group.id}/`, {
        name: groupName.trim()
      });
      
      toast.success(`Grupo atualizado para "${response.data.name}"!`);
      onGroupUpdated(response.data);
      onClose();
    } catch (err) {
      console.error('Error updating group:', err);
      toast.error('Erro ao atualizar grupo');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Editar Grupo">
      <form onSubmit={handleSubmit}>
        <Stack spacing="md">
          <TextInput
            label="Nome do Grupo"
            placeholder="Digite o nome do grupo"
            value={groupName}
            onChange={(event) => setGroupName(event.currentTarget.value)}
            required
            maxLength={150}
          />
          <Group position="right">
            <Button variant="subtle" onClick={onClose} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" loading={isSubmitting}>
              Atualizar Grupo
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

const GroupActions = ({ group, onEdit, onDelete }) => {
  return (
    <Menu shadow="md" width={200}>
      <Menu.Target>
        <ActionIcon variant="subtle" color="gray">
          <IconDots size="1rem" />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Item icon={<IconEdit size={14} />} onClick={() => onEdit(group)}>
          Editar
        </Menu.Item>
        <Menu.Item 
          icon={<IconTrash size={14} />} 
          color="red" 
          onClick={() => onDelete(group)}
        >
          Excluir
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};

const Groups = () => {
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [groupSearch, setGroupSearch] = useState('');
  const selectedGroupRef = useRef(null);
  
  // Modal states
  const [createModalOpened, { open: openCreateModal, close: closeCreateModal }] = useDisclosure(false);
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
  const [groupToEdit, setGroupToEdit] = useState(null);

  const fetchGroups = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await apiInstance.get('authentication/groups/');
      setGroups(response.data);
    } catch (err) {
      console.error('Error fetching groups:', err);
      setError('Erro ao carregar grupos');
      toast.error('Erro ao carregar grupos');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleGroupCreated = useCallback((newGroup) => {
    setGroups(prev => [...prev, newGroup]);
    setSelectedGroup(newGroup);
    selectedGroupRef.current = newGroup;
  }, []);

  const handleGroupUpdated = useCallback((updatedGroup) => {
    setGroups(prev => 
      prev.map(group => 
        group.id === updatedGroup.id ? updatedGroup : group
      )
    );
    
    // Update selected group if it's the one being edited
    if (selectedGroup?.id === updatedGroup.id) {
      setSelectedGroup(updatedGroup);
      selectedGroupRef.current = updatedGroup;
    }
  }, [selectedGroup]);

  const handleEditGroup = useCallback((group) => {
    setGroupToEdit(group);
    openEditModal();
  }, [openEditModal]);

  const handleDeleteGroup = useCallback((group) => {
    const confirmDelete = window.confirm(
      `Tem certeza que deseja excluir o grupo "${group.name}"? Esta ação não pode ser desfeita.`
    );
    
    if (!confirmDelete) return;

    const deleteGroup = async () => {
      try {
        await apiInstance.delete(`authentication/groups/${group.id}/`);
        
        setGroups(prev => prev.filter(g => g.id !== group.id));
        
        // If deleted group was selected, select another one
        if (selectedGroup?.id === group.id) {
          const remainingGroups = groups.filter(g => g.id !== group.id);
          if (remainingGroups.length > 0) {
            setSelectedGroup(remainingGroups[0]);
            selectedGroupRef.current = remainingGroups[0];
          } else {
            setSelectedGroup(null);
            selectedGroupRef.current = null;
          }
        }
        
        toast.success(`Grupo "${group.name}" excluído com sucesso!`);
      } catch (err) {
        console.error('Error deleting group:', err);
        toast.error('Erro ao excluir grupo');
      }
    };

    deleteGroup();
  }, [groups, selectedGroup]);

  // Effect to handle initial group selection and updates
  useEffect(() => {
    if (groups.length > 0) {
      if (selectedGroupRef.current) {
        // Update selected group with fresh data
        const updatedGroup = groups.find(g => g.id === selectedGroupRef.current.id);
        if (updatedGroup) {
          setSelectedGroup(updatedGroup);
          selectedGroupRef.current = updatedGroup;
        } else {
          // Selected group was deleted, select first available
          setSelectedGroup(groups[0]);
          selectedGroupRef.current = groups[0];
        }
      } else {
        // Select first group if none selected
        setSelectedGroup(groups[0]);
        selectedGroupRef.current = groups[0];
      }
    }
  }, [groups]);

  // Sync selectedGroupRef when selectedGroup changes
  useEffect(() => {
    selectedGroupRef.current = selectedGroup;
  }, [selectedGroup]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const filteredGroups = groups.filter(group =>
    group.name.toLowerCase().includes(groupSearch.toLowerCase())
  );

  if (isLoading) {
    return (
      <Box display="flex" style={{ justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loader size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Container size="xl" p="md">
        <Text color="red" size="lg">{error}</Text>
      </Container>
    );
  }

  return (
    <Container size="xl" p="md">
      <Stack spacing="lg">
        <Group position="apart">
          <Title order={2}>Gerenciamento de Grupos</Title>
          <Button 
            leftIcon={<IconPlus size="1rem" />} 
            onClick={openCreateModal}
          >
            Criar Grupo
          </Button>
        </Group>
        
        <Grid>
          <Grid.Col span={4}>
            <Paper p="md" withBorder>
              <Stack spacing="md">
                <Title order={4}>Grupos do Sistema</Title>
                
                <TextInput
                  placeholder="Buscar grupos..."
                  icon={<IconSearch size="0.9rem" />}
                  value={groupSearch}
                  onChange={(event) => setGroupSearch(event.currentTarget.value)}
                />

                <Text size="sm" color="dimmed">
                  Total de grupos: {filteredGroups.length}
                </Text>

                <ScrollArea style={{ height: '60vh' }}>
                  <Stack spacing="xs">
                    {filteredGroups.map(group => (
                      <Paper 
                        key={group.id} 
                        p="md" 
                        radius="md" 
                        withBorder
                        style={{ 
                          cursor: 'pointer',
                          backgroundColor: selectedGroup?.id === group.id ? '#f8f9fa' : 'white'
                        }}
                        onClick={() => setSelectedGroup(group)}
                      >
                        <Group position="apart">
                          <Box style={{ flex: 1 }}>
                            <Text weight={500}>{group.name}</Text>
                          </Box>
                          <Group spacing="xs">
                            {selectedGroup?.id === group.id && (
                              <Badge variant="filled" size="sm">
                                Selecionado
                              </Badge>
                            )}
                            <GroupActions 
                              group={group} 
                              onEdit={handleEditGroup}
                              onDelete={handleDeleteGroup}
                            />
                          </Group>
                        </Group>
                      </Paper>
                    ))}
                  </Stack>
                </ScrollArea>
              </Stack>
            </Paper>
          </Grid.Col>

          <Grid.Col span={8}>
            {selectedGroup ? (
              <GroupDetails 
                key={selectedGroup.id} 
                group={selectedGroup} 
                onGroupUpdate={fetchGroups} 
              />
            ) : (
              <Paper p="md" withBorder>
                <Text color="dimmed" align="center">
                  Selecione um grupo para gerenciar suas permissões
                </Text>
              </Paper>
            )}
          </Grid.Col>
        </Grid>
      </Stack>

      <CreateGroupModal 
        opened={createModalOpened} 
        onClose={closeCreateModal}
        onGroupCreated={handleGroupCreated}
      />

      <EditGroupModal 
        opened={editModalOpened} 
        onClose={closeEditModal}
        group={groupToEdit}
        onGroupUpdated={handleGroupUpdated}
      />
    </Container>
  );
};

export default Groups; 